import { createTransform } from 'redux-persist';

import { initialState as betSlipInitialState } from '@swf/core/models/sports/betSlip';

interface ExpireConfig<PS> {
  persistAt: string;
  secondUntilExpire: number;
  stateAfterExpire: PS;
  ignore: (keyof PS)[];
}

interface InboundStructure<PS> {
  state: PS;
  persistTime: number;
}

const TWENTY_FOUR_HOURS_IN_SECONDS = 60 * 60 * 24; // 24 hours in seconds

/**
 * Transforms the redux-persist data to keep the time reference
 * If the expiration time criteria has been met, it returns the empty/default state
 * @example   
 * persistExpire({
    persistAt: 'betSlip',
    secondUntilExpire: 30,
    stateAfterExpire: betSlipInitialState
  })
 */
export function persistExpire<PS>({
  persistAt,
  secondUntilExpire,
  stateAfterExpire,
  ignore = []
}: ExpireConfig<PS>) {
  return createTransform<PS, InboundStructure<PS>>(
    (inboundState) => {
      const ignored = ignore.reduce((acc, next) => {
        acc[next] = stateAfterExpire[next];
        return acc;
      }, {} as Partial<PS>);

      return {
        state: { ...inboundState, ...ignored },
        persistTime: new Date().getTime()
      };
    },
    ({ state, persistTime }) => {
      const endTime = new Date().getTime();
      const durationInSeconds = (endTime - persistTime) / 1000;

      if (durationInSeconds > secondUntilExpire) {
        return stateAfterExpire;
      }
      return state;
    },
    { whitelist: [persistAt] }
  );
}

const persistTransforms = [
  persistExpire({
    persistAt: 'betSlip',
    secondUntilExpire: TWENTY_FOUR_HOURS_IN_SECONDS,
    stateAfterExpire: betSlipInitialState,
    ignore: ['receipt']
  })
];

export default persistTransforms;
